import { Controller } from "@hotwired/stimulus"

// Seeing an upload before saving it, and aiming the cover's crop (#187). The public header crops
// the cover with object-fit, at a different shape on a phone than on a desktop, so the preview
// shows both frames and the focal point is set by dragging the picture inside one of them - the
// crop is what's being decided, so the picture is what you grab.
//
// Enhancement only. The two number fields under the frames are what gets submitted; they are
// server-rendered and usable on their own, and this hides them once it can offer the drag instead.
// It writes into them the whole time; nothing else carries the value.
const STEP = 5 // ProfilesHelper::COVER_FOCUS_STEP - the cover-focus-* classes come in fives
const SLACK = 2 // px of overflow too small to pan, and small enough to be rounding
const KEYS = {
  ArrowLeft: [-STEP, 0],
  ArrowRight: [STEP, 0],
  ArrowUp: [0, -STEP],
  ArrowDown: [0, STEP]
}

export default class extends Controller {
  static targets = [
    "avatarImage", "avatarPlaceholder", "coverImage", "coverPlaceholder", "frame",
    "focusRoot", "focusXInput", "focusYInput", "hint", "fallback", "image", "placeholder"
  ]

  connect() {
    this.objectUrls = {}
    this.pan = null
    if (this.hasHintTarget) this.hintTarget.classList.remove("hidden")
    if (this.hasFallbackTarget) this.fallbackTarget.classList.add("hidden")
    if (this.#hasFocusTargets()) this.#applyFocus()
    if (this.hasFrameTarget) this.#markPannableAxes()
  }

  disconnect() {
    this.#revokeObjectUrls()
  }

  previewAvatar(event) {
    const url = this.#objectUrl("avatar", event.target.files[0])
    if (!url) return

    this.#show(this.avatarImageTarget, url)
    if (this.hasAvatarPlaceholderTarget) this.avatarPlaceholderTarget.classList.add("hidden")
  }

  previewCover(event) {
    const url = this.#objectUrl("cover", event.target.files[0])
    if (!url) return

    // A focal point aimed at the previous picture means nothing in this one.
    this.#write(50, 50)
    this.coverImageTargets.forEach((image) => this.#show(image, url))
    this.coverPlaceholderTargets.forEach((placeholder) => placeholder.classList.add("hidden"))
  }

  previewImage(event) {
    const url = this.#objectUrl("image", event.target.files[0])
    if (!url) return

    this.imageTargets.forEach((image) => this.#show(image, url))
    this.placeholderTargets.forEach((placeholder) => placeholder.classList.add("hidden"))
  }

  // naturalWidth is 0 until the browser has decoded the file, so how far a picture can travel is
  // only knowable once it has loaded - at first paint and after every replacement.
  coverLoaded() {
    this.#markPannableAxes()
  }

  startDrag(event) {
    const room = this.#room(event.currentTarget)
    if (!room.x && !room.y) return

    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    this.pan = {
      room,
      pointerX: event.clientX,
      pointerY: event.clientY,
      focusX: this.#focus(this.focusXInputTarget),
      focusY: this.#focus(this.focusYInputTarget)
    }
  }

  drag(event) {
    if (!this.pan) return

    const { room, focusX, focusY } = this.pan
    // Dragging the picture right uncovers what sat off its left edge, so the focal point travels
    // the opposite way to the pointer.
    const x = room.x ? focusX - ((event.clientX - this.pan.pointerX) / room.x) * 100 : focusX
    const y = room.y ? focusY - ((event.clientY - this.pan.pointerY) / room.y) * 100 : focusY

    this.#write(x, y)
  }

  endDrag() {
    this.pan = null
  }

  key(event) {
    const move = KEYS[event.key]
    if (!move) return

    event.preventDefault()
    this.#write(this.#focus(this.focusXInputTarget) + move[0],
                this.#focus(this.focusYInputTarget) + move[1])
  }

  // How many pixels of picture hang outside the frame. object-fit: cover scales until the frame
  // is covered, so the slack is on one axis only - the other is exactly full.
  #room(frame) {
    const image = this.coverImageTargets.find((candidate) => frame.contains(candidate))
    const bounds = frame.getBoundingClientRect()
    if (!image?.naturalWidth || !bounds.width) return { x: 0, y: 0 }

    const scale = Math.max(bounds.width / image.naturalWidth,
                           bounds.height / image.naturalHeight)

    return {
      x: this.#slack(image.naturalWidth * scale - bounds.width),
      y: this.#slack(image.naturalHeight * scale - bounds.height)
    }
  }

  // A frame and a picture that match to within a pixel or two are the same picture as far as
  // anyone can see; treating that leftover as room to pan turns a rounding error into an axis that
  // jitters under the finger.
  #slack(pixels) {
    return pixels > SLACK ? pixels : 0
  }

  #markPannableAxes() {
    this.frameTargets.forEach((frame) => {
      const room = this.#room(frame)
      frame.classList.toggle("image-preview__frame--pan-x", room.x > 0)
      frame.classList.toggle("image-preview__frame--pan-y", room.y > 0)
    })
  }

  // The only way the focal point ever changes: drag, arrow key and a replaced picture all come
  // through here, and the fields are the state - nothing is cached alongside them.
  #write(x, y) {
    if (!this.#hasFocusTargets()) return

    this.focusXInputTarget.value = this.#snap(x)
    this.focusYInputTarget.value = this.#snap(y)
    this.#applyFocus()
  }

  // The preview and the saved value are the same number: the class the frames read is the class
  // the public header will get, so what you drag to is what the profile gets.
  #applyFocus() {
    const x = this.#focus(this.focusXInputTarget)
    const y = this.#focus(this.focusYInputTarget)

    for (let value = 0; value <= 100; value += STEP) {
      this.focusRootTarget.classList.toggle(`cover-focus-x-${value}`, value === x)
      this.focusRootTarget.classList.toggle(`cover-focus-y-${value}`, value === y)
    }
  }

  #hasFocusTargets() {
    return this.hasFocusRootTarget && this.hasFocusXInputTarget && this.hasFocusYInputTarget
  }

  #focus(input) {
    return this.#snap(input.value)
  }

  // Percentages land on a class, and classes come in fives (cover_focus.css). An empty or junk
  // field reads as dead center rather than as NaN, which would take every cover-focus class off
  // the element and leave the crop wherever the CSS fallback puts it.
  #snap(value) {
    const numeric = Number.parseFloat(value)
    if (Number.isNaN(numeric)) return 50

    return Math.min(100, Math.max(0, Math.round(numeric / STEP) * STEP))
  }

  #show(image, url) {
    image.dispatchEvent(new CustomEvent("loadable:begin", { bubbles: true, detail: { source: url } }))
    image.src = url
    image.classList.remove("hidden")
  }

  // An object URL pins the file in memory until it is revoked, and picking a third cover would
  // otherwise leave the first two pinned for the life of the page - hence one slot per input,
  // released as it is reused and again when the controller goes away.
  #objectUrl(slot, file) {
    if (!file) return null

    this.#revokeObjectUrl(slot)
    const url = URL.createObjectURL(file)
    this.objectUrls[slot] = url
    return url
  }

  #revokeObjectUrl(slot) {
    if (!this.objectUrls[slot]) return

    URL.revokeObjectURL(this.objectUrls[slot])
    delete this.objectUrls[slot]
  }

  #revokeObjectUrls() {
    Object.keys(this.objectUrls).forEach((slot) => this.#revokeObjectUrl(slot))
  }
}
